import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import Modal from '../components/Modal';
import CustomerSearchSelect from '../components/CustomerSearchSelect';
import authFetch from '../utils/api';
import './Invoices.css';

// Phone number formatting utility
const formatPhoneNumber = (value) => {
  // Remove all non-numeric characters
  const phoneNumber = value.replace(/\D/g, '');

  // Format as XXX-XXX-XXXX
  if (phoneNumber.length <= 3) {
    return phoneNumber;
  } else if (phoneNumber.length <= 6) {
    return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3)}`;
  } else {
    return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
  }
};

// Format 24-hour time to 12-hour format
const formatTime12Hour = (time24) => {
  if (!time24) return '';
  const [hours, minutes] = time24.split(':');
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${hour12}:${minutes} ${ampm}`;
};

const Invoices = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [services, setServices] = useState({ packages: [], addons: [] });
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showNewCustomerForm, setShowNewCustomerForm] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [filter, setFilter] = useState('all');

  const [formData, setFormData] = useState({
    customer_id: '',
    vehicles: [{ id: Date.now(), label: '', vehicle_type: 'car', selectedServices: [] }],
    tax_rate: 0,
    tax_enabled: false,
    due_date: '',
    scheduled_time: '09:00',
    location_address: '',
    location_city: '',
    location_state: 'TX',
    location_zip: '',
    notes: ''
  });

  const [newCustomerData, setNewCustomerData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    addVehicle: false,
    vehicle_year: '',
    vehicle_make: '',
    vehicle_model: '',
    vehicle_color: '',
    vehicle_type: 'sedan'
  });

  const [showConflictModal, setShowConflictModal] = useState(false);
  const [conflictingAppointments, setConflictingAppointments] = useState([]);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [gmailConnected, setGmailConnected] = useState(false);
  const [copyLinkLabel, setCopyLinkLabel] = useState('Copy Payment Link');
  const [copyReceiptLabel, setCopyReceiptLabel] = useState('Text Receipt');
  const [customerAddresses, setCustomerAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState('');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [payingInvoiceId, setPayingInvoiceId] = useState(null);
  const [newlyCreatedCustomerId, setNewlyCreatedCustomerId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showRevisitModal, setShowRevisitModal] = useState(false);
  const [revisitInvoiceId, setRevisitInvoiceId] = useState(null);
  const [revisitDate, setRevisitDate] = useState('');
  const [submittingRevisit, setSubmittingRevisit] = useState(false);

  const getPaymentMethodDisplay = (methodId) => {
    if (!methodId) return null;
    const method = paymentMethods.find(m => m.id === methodId);
    if (!method) return null;
    if (method.icon) {
      return <span title={method.name} style={{ fontSize: '1rem', lineHeight: 1 }}>{method.icon}</span>;
    }
    if (method.logo === 'zelle') return (
      <svg title={method.name} viewBox="0 0 24 24" width="16" height="16" fill={method.color} style={{ display: 'inline', verticalAlign: 'middle' }}>
        <title>{method.name}</title>
        <path d="M13.559 24h-2.841a.483.483 0 0 1-.483-.483v-6.628H5.685a.69.69 0 0 1-.493-1.173l9.532-9.716a.483.483 0 0 1 .827.342v6.379h4.765a.69.69 0 0 1 .492 1.173l-6.766 6.902v2.721a.483.483 0 0 1-.483.483zM7.616 15.345h3.86c.267 0 .483.217.483.484v4.972l4.834-4.934h-4.075a.483.483 0 0 1-.483-.483V9.799l-4.619 5.546z"/>
      </svg>
    );
    if (method.logo === 'cashapp') return (
      <svg title={method.name} viewBox="0 0 24 24" width="16" height="16" fill={method.color} style={{ display: 'inline', verticalAlign: 'middle' }}>
        <title>{method.name}</title>
        <path d="M23.59 3.47A5.1 5.1 0 0 0 20.53.41C19.63.14 18.46 0 17 0H7C5.54 0 4.37.14 3.47.41A5.1 5.1 0 0 0 .41 3.47C.14 4.37 0 5.54 0 7v10c0 1.46.14 2.63.41 3.53a5.1 5.1 0 0 0 3.06 3.06c.9.27 2.07.41 3.53.41h10c1.46 0 2.63-.14 3.53-.41a5.1 5.1 0 0 0 3.06-3.06c.27-.9.41-2.07.41-3.53V7c0-1.46-.14-2.63-.41-3.53zm-6.17 6.72l-.6.6a.47.47 0 0 1-.6.06 2.8 2.8 0 0 0-1.47-.42c-.55 0-1.04.15-1.32.45-.28.28-.32.56-.32.73 0 .21.04.39.2.55.17.18.52.36 1.04.54l.9.3c.74.24 1.38.54 1.87 1 .5.48.79 1.13.79 1.96 0 .9-.27 1.68-.98 2.29-.7.6-1.6.92-2.72.98v1.06a.47.47 0 0 1-.47.47h-.9a.47.47 0 0 1-.48-.47v-1.1a5.04 5.04 0 0 1-2.4-.95.47.47 0 0 1-.05-.67l.6-.6a.47.47 0 0 1 .61-.05 3.42 3.42 0 0 0 1.81.54c.62 0 1.1-.14 1.4-.4.28-.26.42-.58.42-.97 0-.27-.08-.48-.25-.67-.2-.2-.56-.4-1.12-.58l-.87-.29c-.7-.23-1.3-.55-1.77-1a2.4 2.4 0 0 1-.72-1.84c0-.86.3-1.57.92-2.14.61-.57 1.45-.88 2.5-.93V6.19c0-.26.21-.47.47-.47h.9c.27 0 .48.21.48.47v1.03a4.63 4.63 0 0 1 2.07.73c.2.13.24.4.1.6z"/>
      </svg>
    );
    if (method.logo === 'venmo') return (
      <svg title={method.name} viewBox="0 0 24 24" width="16" height="16" fill={method.color} style={{ display: 'inline', verticalAlign: 'middle' }}>
        <title>{method.name}</title>
        <path d="M19.616 2.252c.756 1.26 1.092 2.553 1.092 4.185 0 5.22-4.431 11.982-8.04 16.729H5.25L2.291 3.844l6.156-.605 1.671 13.564c1.545-2.533 3.457-6.493 3.457-9.2 0-1.564-.267-2.635-.629-3.513l6.67-1.838z"/>
      </svg>
    );
    if (method.logo === 'paypal') return (
      <svg title={method.name} viewBox="0 0 24 24" width="16" height="16" fill={method.color} style={{ display: 'inline', verticalAlign: 'middle' }}>
        <title>{method.name}</title>
        <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 0 0-.607-.541c-.013.076-.026.175-.041.254-.93 4.778-4.005 7.201-9.138 7.201h-2.19a.563.563 0 0 0-.556.479l-1.187 7.527h-.506l-.24 1.516a.56.56 0 0 0 .554.647h3.882c.46 0 .85-.334.922-.788.06-.26.76-4.852.816-5.09a.932.932 0 0 1 .923-.788h.58c3.76 0 6.705-1.528 7.565-5.946.36-1.847.174-3.388-.777-4.471z"/>
      </svg>
    );
    if (method.logo === 'apple') return (
      <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: '#000', borderRadius: '3px', padding: '1px', verticalAlign: 'middle' }}>
        <svg title={method.name} viewBox="0 0 24 24" width="14" height="14" fill="white" style={{ display: 'block' }}>
          <title>{method.name}</title>
          <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701"/>
        </svg>
      </span>
    );
    if (method.logo === 'stripe') return (
      <svg title={method.name} viewBox="0 0 24 24" width="16" height="16" fill={method.color} style={{ display: 'inline', verticalAlign: 'middle' }}>
        <title>{method.name}</title>
        <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.594-7.305h.003z"/>
      </svg>
    );
    return null;
  };

  // Payment methods with icons (using inline SVG for simple icons, brand colors for logos)
  const paymentMethods = [
    { id: 'cash', name: 'Cash', icon: '💵', color: '#2e7d32' },
    { id: 'zelle', name: 'Zelle', icon: null, logo: 'zelle', color: '#6d1ed4' },
    { id: 'cashapp', name: 'Cash App', icon: null, logo: 'cashapp', color: '#00d632' },
    { id: 'venmo', name: 'Venmo', icon: null, logo: 'venmo', color: '#008cff' },
    { id: 'paypal', name: 'PayPal', icon: null, logo: 'paypal', color: '#003087' },
    { id: 'apple_cash', name: 'Apple Cash', icon: null, logo: 'apple', color: '#000000' },
    { id: 'stripe', name: 'Stripe', icon: null, logo: 'stripe', color: '#635bff' },
    { id: 'tap_to_pay', name: 'Tap to Pay (in person)', icon: '📲', color: '#635bff' },
    { id: 'comp', name: 'Comped / Giveaway', icon: '🎁', color: '#c9a227' }
  ];

  useEffect(() => {
    fetchData();
    checkGmailStatus();
  }, [filter]);

  // Deep link support: /invoices?open=<id> auto-opens that invoice's detail modal
  useEffect(() => {
    const openId = searchParams.get('open');
    if (openId) {
      fetchInvoiceDetail(openId);
      const next = new URLSearchParams(searchParams);
      next.delete('open');
      setSearchParams(next, { replace: true });
    }
  }, []);

  const checkGmailStatus = async () => {
    try {
      const response = await fetch('/api/gmail/status');
      const data = await response.json();
      setGmailConnected(data.connected);
    } catch (error) {
      console.error('Error checking Gmail status:', error);
    }
  };

  const fetchData = async () => {
    try {
      const statusParam = filter !== 'all' ? `?status=${filter}` : '';
      const [invoicesRes, customersRes, servicesRes] = await Promise.all([
        fetch(`/api/invoices${statusParam}`),
        fetch('/api/customers'),
        fetch('/api/services/grouped')
      ]);

      const [invoicesData, customersData, servicesData] = await Promise.all([
        invoicesRes.json(),
        customersRes.json(),
        servicesRes.json()
      ]);

      setInvoices(invoicesData);
      setCustomers(customersData);
      setServices(servicesData);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchInvoiceDetail = async (id) => {
    try {
      const response = await fetch(`/api/invoices/${id}`);
      const data = await response.json();
      setSelectedInvoice(data);
      setShowDetailModal(true);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleCustomerSelect = async (customerId) => {
    if (!customerId) {
      setFormData({ ...formData, customer_id: '' });
      setCustomerAddresses([]);
      setSelectedAddressId('');
      return;
    }

    try {
      const [customerRes, addressRes] = await Promise.all([
        fetch(`/api/customers/${customerId}`),
        fetch(`/api/customers/${customerId}/addresses`)
      ]);
      const customer = await customerRes.json();
      const savedAddresses = await addressRes.json();

      // Build combined list: profile address first (if exists), then saved addresses
      const combined = [];
      if (customer.address) {
        combined.push({ id: 'profile', name: 'Main Address', address: customer.address, city: customer.city, state: customer.state, zip: customer.zip });
      }
      combined.push(...savedAddresses);
      setCustomerAddresses(combined);

      // Auto-select: default saved address, or profile address, or nothing
      const defaultSaved = savedAddresses.find(a => a.is_default === 1);
      const autoSelect = defaultSaved || combined[0];

      if (autoSelect) {
        setSelectedAddressId(autoSelect.id.toString());
        setFormData({
          ...formData,
          customer_id: customerId,
          location_address: autoSelect.address || '',
          location_city: autoSelect.city || '',
          location_state: autoSelect.state || 'TX',
          location_zip: autoSelect.zip || ''
        });
      } else {
        setSelectedAddressId('');
        setFormData({ ...formData, customer_id: customerId });
      }
    } catch (error) {
      console.error('Error fetching customer data:', error);
      const customer = customers.find(c => c.id.toString() === customerId);
      if (customer?.address) {
        setFormData({
          ...formData,
          customer_id: customerId,
          location_address: customer.address || '',
          location_city: customer.city || '',
          location_state: customer.state || 'TX',
          location_zip: customer.zip || ''
        });
      }
    }
  };

  const handleAddressSelect = (addressId) => {
    setSelectedAddressId(addressId);

    if (addressId === 'custom') {
      // Clear fields for custom entry
      setFormData({
        ...formData,
        location_address: '',
        location_city: '',
        location_state: 'TX',
        location_zip: ''
      });
      return;
    }

    const address = customerAddresses.find(a => a.id.toString() === addressId);
    if (address) {
      setFormData({
        ...formData,
        location_address: address.address || '',
        location_city: address.city || '',
        location_state: address.state || 'TX',
        location_zip: address.zip || ''
      });
    }
  };

  const handleCreateCustomer = async () => {
    if (!newCustomerData.first_name || !newCustomerData.last_name || !newCustomerData.phone) {
      alert('Please fill in first name, last name, and phone');
      return;
    }

    if (newCustomerData.addVehicle && (!newCustomerData.vehicle_make || !newCustomerData.vehicle_model)) {
      alert('Please fill in vehicle make and model');
      return;
    }

    try {
      const response = await fetch('/api/customers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: newCustomerData.first_name,
          last_name: newCustomerData.last_name,
          phone: newCustomerData.phone,
          email: newCustomerData.email,
          address: formData.location_address,
          city: formData.location_city,
          state: formData.location_state,
          zip: formData.location_zip
        })
      });

      if (response.ok) {
        const newCustomer = await response.json();

        // Create vehicle if checkbox was checked
        if (newCustomerData.addVehicle && newCustomerData.vehicle_make && newCustomerData.vehicle_model) {
          await fetch('/api/vehicles', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              customer_id: newCustomer.id,
              year: newCustomerData.vehicle_year || null,
              make: newCustomerData.vehicle_make,
              model: newCustomerData.vehicle_model,
              color: newCustomerData.vehicle_color || null,
              vehicle_type: newCustomerData.vehicle_type
            })
          });
        }

        // Refresh customers list
        const customersRes = await fetch('/api/customers');
        const customersData = await customersRes.json();
        setCustomers(customersData);
        // Auto-select the new customer and track that we created them
        setFormData({ ...formData, customer_id: newCustomer.id.toString() });
        setNewlyCreatedCustomerId(newCustomer.id);
        // Hide the form and reset
        setShowNewCustomerForm(false);
        setNewCustomerData({
          first_name: '', last_name: '', phone: '', email: '',
          addVehicle: false, vehicle_year: '', vehicle_make: '',
          vehicle_model: '', vehicle_color: '', vehicle_type: 'sedan'
        });
      }
    } catch (error) {
      console.error('Error creating customer:', error);
    }
  };

  const fetchServicesForPackages = async (selectedPackageIds) => {
    try {
      // If any packages are selected, use the first one to filter add-ons
      const packageId = selectedPackageIds.length > 0 ? selectedPackageIds[0] : null;
      const queryParam = packageId ? `?package_id=${packageId}` : '';

      const servicesRes = await fetch(`/api/services/grouped${queryParam}`);
      const servicesData = await servicesRes.json();
      setServices(servicesData);
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };

  const addVehicle = () => {
    setFormData({
      ...formData,
      vehicles: [
        ...formData.vehicles,
        { id: Date.now(), label: '', vehicle_type: 'car', selectedServices: [] }
      ]
    });
  };

  const removeVehicle = (vehicleIndex) => {
    setFormData({
      ...formData,
      vehicles: formData.vehicles.filter((_, i) => i !== vehicleIndex)
    });
  };

  const updateVehicleLabel = (vehicleIndex, label) => {
    const updatedVehicles = formData.vehicles.map((v, i) =>
      i === vehicleIndex ? { ...v, label } : v
    );
    setFormData({ ...formData, vehicles: updatedVehicles });
  };

  const toggleService = (vehicleIndex, service) => {
    const vehicle = formData.vehicles[vehicleIndex];
    const isSelected = vehicle.selectedServices.find(s => s.id === service.id);
    let newSelectedServices;

    if (isSelected) {
      newSelectedServices = vehicle.selectedServices.filter(s => s.id !== service.id);
    } else {
      const price = vehicle.vehicle_type === 'truck'
        ? (service.base_price_truck || service.base_price_car)
        : service.base_price_car;

      newSelectedServices = [...vehicle.selectedServices, {
        id: service.id,
        name: service.name,
        price: price,
        category: service.category,
        customNote: ''
      }];
    }

    const updatedVehicles = formData.vehicles.map((v, i) =>
      i === vehicleIndex ? { ...v, selectedServices: newSelectedServices } : v
    );
    setFormData({ ...formData, vehicles: updatedVehicles });

    // If a package was toggled, refetch services to update available add-ons
    if (service.category === 'package') {
      const selectedPackageIds = newSelectedServices
        .filter(s => s.category === 'package')
        .map(s => s.id);
      fetchServicesForPackages(selectedPackageIds);
    }
  };

  const isServiceSelected = (vehicleIndex, serviceId) => {
    return formData.vehicles[vehicleIndex]?.selectedServices.some(s => s.id === serviceId);
  };

  const updateServicePrice = (vehicleIndex, serviceId, newPrice) => {
    const updatedVehicles = formData.vehicles.map((v, i) =>
      i === vehicleIndex ? {
        ...v,
        selectedServices: v.selectedServices.map(s =>
          s.id === serviceId ? { ...s, price: parseFloat(newPrice) || 0 } : s
        )
      } : v
    );
    setFormData({ ...formData, vehicles: updatedVehicles });
  };

  const updateServiceNote = (vehicleIndex, serviceId, note) => {
    const updatedVehicles = formData.vehicles.map((v, i) =>
      i === vehicleIndex ? {
        ...v,
        selectedServices: v.selectedServices.map(s =>
          s.id === serviceId ? { ...s, customNote: note } : s
        )
      } : v
    );
    setFormData({ ...formData, vehicles: updatedVehicles });
  };

  const handleVehicleTypeChange = (vehicleIndex, newVehicleType) => {
    const vehicle = formData.vehicles[vehicleIndex];
    const updatedServices = vehicle.selectedServices.map(selectedService => {
      const fullService = [...services.packages, ...services.addons].find(s => s.id === selectedService.id);
      if (fullService) {
        const newPrice = newVehicleType === 'truck'
          ? (fullService.base_price_truck || fullService.base_price_car)
          : fullService.base_price_car;
        return { ...selectedService, price: newPrice };
      }
      return selectedService;
    });

    const updatedVehicles = formData.vehicles.map((v, i) =>
      i === vehicleIndex ? { ...v, vehicle_type: newVehicleType, selectedServices: updatedServices } : v
    );
    setFormData({ ...formData, vehicles: updatedVehicles });
  };

  const calculateSubtotal = () => {
    return formData.vehicles.reduce((total, vehicle) =>
      total + vehicle.selectedServices.reduce((sum, s) => sum + (s.price || 0), 0), 0
    );
  };

  const checkForConflicts = async () => {
    try {
      const url = `/api/appointments/check-conflicts?date=${formData.due_date}&time=${formData.scheduled_time}`;
      const response = await fetch(url);
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error checking conflicts:', error);
      return { hasConflict: false, conflicts: [] };
    }
  };

  const submitInvoice = async () => {
    if (submitting) return; // Prevent double submission
    setSubmitting(true);

    try {
      const items = formData.vehicles.flatMap(vehicle =>
        vehicle.selectedServices.map(service => {
          const baseName = service.customNote ? `${service.name} — ${service.customNote}` : service.name;
          return {
            description: vehicle.label ? `${vehicle.label} — ${baseName}` : baseName,
            quantity: 1,
            unit_price: service.price
          };
        })
      );

      if (editingInvoice) {
        // UPDATE mode - PUT request
        const response = await fetch(`/api/invoices/${editingInvoice.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items,
            tax_rate: formData.tax_rate,
            due_date: formData.due_date,
            notes: formData.notes,
            status: editingInvoice.status
          })
        });

        if (response.ok) {
          setShowModal(false);
          setEditingInvoice(null);
          resetForm();
          fetchData();
        }
      } else {
        // CREATE mode - POST request
        const response = await fetch('/api/invoices', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            customer_id: formData.customer_id,
            items,
            tax_rate: formData.tax_rate,
            due_date: formData.due_date,
            notes: formData.notes
          })
        });

        if (response.ok) {
          const invoiceData = await response.json();

          // Auto-create appointment if date and time are provided
          if (formData.due_date && formData.scheduled_time) {
            // Only include services that have valid service IDs
            const appointmentServices = formData.vehicles.flatMap(vehicle =>
              vehicle.selectedServices
                .filter(service => service.id !== null && service.id !== undefined)
                .map(service => ({
                  service_id: service.id,
                  price: service.price
                }))
            );

            // Create appointment even if no services (services are optional)
            const aptResponse = await fetch('/api/appointments', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                customer_id: formData.customer_id,
                scheduled_date: formData.due_date,
                scheduled_time: formData.scheduled_time,
                location_address: formData.location_address,
                location_city: formData.location_city,
                location_state: formData.location_state,
                location_zip: formData.location_zip,
                notes: formData.notes,
                services: appointmentServices.length > 0 ? appointmentServices : [],
                skip_auto_invoice: true  // Invoice already created, don't create another
              })
            });

            if (aptResponse.ok) {
              const appointmentData = await aptResponse.json();

              // Link invoice to appointment
              await fetch(`/api/invoices/${invoiceData.id}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  appointment_id: appointmentData.id
                })
              });
            } else {
              const errorText = await aptResponse.text();
              console.error('[Client] Failed to create appointment:', aptResponse.status, errorText);
              alert(`Invoice created but appointment creation failed: ${errorText}\n\nPlease create appointment manually from the calendar.`);
            }
          }

          // Update newly created customer's address if location was provided
          if (newlyCreatedCustomerId && formData.location_address) {
            try {
              // Fetch current customer data first
              const customerRes = await fetch(`/api/customers/${newlyCreatedCustomerId}`);
              const customerData = await customerRes.json();

              // Update with address merged in
              await fetch(`/api/customers/${newlyCreatedCustomerId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  first_name: customerData.first_name,
                  last_name: customerData.last_name,
                  email: customerData.email,
                  phone: customerData.phone,
                  address: formData.location_address,
                  city: formData.location_city,
                  state: formData.location_state,
                  zip: formData.location_zip,
                  notes: customerData.notes
                })
              });
            } catch (err) {
              console.error('[Client] Failed to update customer address:', err);
            }
          }

          setShowModal(false);
          resetForm();
          fetchData();
        }
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmConflict = async () => {
    setShowConflictModal(false);
    setConflictingAppointments([]);
    await submitInvoice();
  };

  const handleCancelConflict = () => {
    setShowConflictModal(false);
    setConflictingAppointments([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Prevent double submission
    if (submitting) return;

    const totalServices = formData.vehicles.reduce((sum, v) => sum + v.selectedServices.length, 0);
    if (totalServices === 0) {
      alert('Please select at least one service');
      return;
    }

    if (formData.due_date && formData.scheduled_time) {
      // Validate that the date/time is not in the past
      const appointmentDateTime = new Date(`${formData.due_date}T${formData.scheduled_time}`);
      const now = new Date();
      if (appointmentDateTime < now) {
        const proceed = window.confirm('The selected date/time is in the past. Do you want to continue?');
        if (!proceed) return;
      }

      // Check for conflicts (only for new invoices with appointments)
      if (!editingInvoice) {
        const { hasConflict, conflicts } = await checkForConflicts();
        if (hasConflict) {
          setConflictingAppointments(conflicts);
          setShowConflictModal(true);
          return;
        }
      }
    }

    await submitInvoice();
  };

  const handleStatusUpdate = async (invoiceId, newStatus, paymentMethod = null) => {
    try {
      await fetch(`/api/invoices/${invoiceId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus, payment_method: paymentMethod })
      });
      fetchData();
      if (selectedInvoice?.id === invoiceId) {
        fetchInvoiceDetail(invoiceId);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleMarkAsPaidClick = (invoiceId) => {
    setPayingInvoiceId(invoiceId);
    setShowPaymentModal(true);
  };

  const handleOpenRevisit = (invoiceId) => {
    setRevisitInvoiceId(invoiceId);
    setRevisitDate('');
    setShowRevisitModal(true);
  };

  const handleSubmitRevisit = async (e) => {
    e.preventDefault();
    if (!revisitDate || submittingRevisit) return;
    setSubmittingRevisit(true);
    try {
      const response = await fetch(`/api/invoices/${revisitInvoiceId}/revisit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date: revisitDate })
      });
      if (response.ok) {
        const newInvoice = await response.json();
        setShowRevisitModal(false);
        await fetchData();
        await fetchInvoiceDetail(newInvoice.id);
      } else {
        const err = await response.json();
        alert(err.error || 'Failed to schedule revisit');
      }
    } catch (error) {
      console.error('Error scheduling revisit:', error);
      alert('Failed to schedule revisit');
    } finally {
      setSubmittingRevisit(false);
    }
  };

  const handleMarkUnpaid = async (invoiceId) => {
    await handleStatusUpdate(invoiceId, 'sent');
  };

  const handleCalculateMileage = async (invoiceId) => {
    try {
      const response = await authFetch(`/api/mileage/calculate-from-invoice/${invoiceId}`, {
        method: 'POST'
      });
      const data = await response.json();

      if (response.ok) {
        alert(`Mileage calculated!\n\nTo job: ${data.toDestination} miles\nReturn home: ${data.returnHome} miles\nTotal: ${data.totalMiles} miles`);
      } else {
        alert(`Failed to calculate mileage: ${data.error}`);
      }
    } catch (error) {
      console.error('Error calculating mileage:', error);
      alert('Failed to calculate mileage. Please try again.');
    }
  };

  const handlePaymentMethodSelect = async (paymentMethod) => {
    if (payingInvoiceId) {
      await handleStatusUpdate(payingInvoiceId, 'paid', paymentMethod);
      setShowPaymentModal(false);
      setPayingInvoiceId(null);
      setShowDetailModal(false);
    }
  };

  const handleEditInvoice = async (invoice) => {
    try {
      // Fetch full invoice details with items
      const response = await fetch(`/api/invoices/${invoice.id}`);
      const data = await response.json();

      setEditingInvoice(data);

      // Convert invoice items to selectedServices format inside a single vehicle
      const selectedServices = data.items.map(item => ({
        id: null, // We don't have service IDs from items, they're just descriptions
        name: item.description,
        price: parseFloat(item.unit_price),
        category: 'addon' // Default, doesn't matter for editing
      }));

      setFormData({
        customer_id: data.customer_id.toString(),
        vehicles: [{ id: Date.now(), label: '', vehicle_type: 'car', selectedServices }],
        tax_rate: data.tax_rate,
        tax_enabled: data.tax_rate > 0,
        due_date: data.due_date || '',
        scheduled_time: data.appointment?.scheduled_time || '09:00',
        location_address: data.appointment?.location_address || '',
        location_city: data.appointment?.location_city || '',
        location_state: data.appointment?.location_state || 'TX',
        location_zip: data.appointment?.location_zip || '',
        notes: data.notes || ''
      });

      setShowDetailModal(false);
      setShowModal(true);
    } catch (error) {
      console.error('Error loading invoice for edit:', error);
    }
  };

  const handleDeleteInvoice = async (invoiceId) => {
    if (!window.confirm('Move this invoice to trash? You can restore it later from the Trash page.')) {
      return;
    }

    try {
      const response = await fetch(`/api/invoices/${invoiceId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        setShowDetailModal(false);
        setSelectedInvoice(null);
        fetchData();
      } else {
        const error = await response.json();
        alert(`Failed to delete invoice: ${error.error}`);
      }
    } catch (error) {
      console.error('Error deleting invoice:', error);
      alert('Failed to delete invoice. Please try again.');
    }
  };

  const handleSendEmail = async (invoiceId, isReceipt = false) => {
    if (!gmailConnected) {
      alert('Gmail is not connected. Please connect your Gmail account in Settings first.');
      return;
    }

    if (!selectedInvoice?.email) {
      alert('This customer does not have an email address. Please add one to their profile first.');
      return;
    }

    setSendingEmail(true);
    try {
      const response = await fetch(`/api/gmail/send-invoice/${invoiceId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isReceipt })
      });

      const data = await response.json();

      if (response.ok) {
        alert(`${isReceipt ? 'Receipt' : 'Invoice'} sent successfully to ${data.sentTo}`);
        fetchInvoiceDetail(invoiceId);
        fetchData();
      } else {
        alert(`Failed to send ${isReceipt ? 'receipt' : 'invoice'}: ${data.error}`);
      }
    } catch (error) {
      console.error('Error sending email:', error);
      alert('Failed to send. Please try again.');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleCopyPaymentLink = async (invoiceId) => {
    try {
      const res = await fetch(`/api/invoices/${invoiceId}/public-link`, { method: 'POST' });
      const data = await res.json();
      if (data.url) {
        const firstName = selectedInvoice?.first_name || '';
        const rawDate = selectedInvoice?.appointment?.scheduled_date || selectedInvoice?.due_date;
        const dateStr = rawDate ? format(parseISO(rawDate), 'MMM d, yyyy') : 'your recent detail';
        const message = `Here is the payment link for your detail on ${dateStr}, thank you again ${firstName} for scheduling with us! ${data.url}\n\nIf you could take a moment to leave us a review on Google we would really appreciate it! https://g.page/r/CcLxlV_CRv8EEBM/review`;

        if (navigator.share) {
          await navigator.share({ text: message });
        } else {
          let copied = false;
          if (navigator.clipboard && navigator.clipboard.writeText) {
            try {
              await navigator.clipboard.writeText(message);
              copied = true;
            } catch (e) {
              // fall through to execCommand fallback
            }
          }
          if (!copied) {
            const textarea = document.createElement('textarea');
            textarea.value = message;
            textarea.style.position = 'fixed';
            textarea.style.opacity = '0';
            document.body.appendChild(textarea);
            textarea.focus();
            textarea.select();
            copied = document.execCommand('copy');
            document.body.removeChild(textarea);
          }
          if (copied) {
            setCopyLinkLabel('Copied!');
            setTimeout(() => setCopyLinkLabel('Copy Payment Link'), 2500);
          } else {
            alert('Failed to copy link. Please try again.');
          }
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        alert('Failed to copy link. Please try again.');
      }
    }
  };

  const handleCopyReceipt = async (invoiceId) => {
    try {
      const res = await fetch(`/api/invoices/${invoiceId}/public-link`, { method: 'POST' });
      const data = await res.json();
      if (data.url) {
        const firstName = selectedInvoice?.first_name || '';
        const rawDate = selectedInvoice?.appointment?.scheduled_date || selectedInvoice?.due_date;
        const dateStr = rawDate ? format(parseISO(rawDate), 'MMM d, yyyy') : 'your recent detail';
        const message = `Here is your receipt for your detail on ${dateStr}, thank you again ${firstName} for your business! ${data.url}\n\nIf you could take a moment to leave us a review on Google we would really appreciate it! https://g.page/r/CcLxlV_CRv8EEBM/review`;

        if (navigator.share) {
          await navigator.share({ text: message });
        } else {
          let copied = false;
          if (navigator.clipboard && navigator.clipboard.writeText) {
            try {
              await navigator.clipboard.writeText(message);
              copied = true;
            } catch (e) {
              // fall through to execCommand fallback
            }
          }
          if (!copied) {
            const textarea = document.createElement('textarea');
            textarea.value = message;
            textarea.style.position = 'fixed';
            textarea.style.opacity = '0';
            document.body.appendChild(textarea);
            textarea.focus();
            textarea.select();
            copied = document.execCommand('copy');
            document.body.removeChild(textarea);
          }
          if (copied) {
            setCopyReceiptLabel('Copied!');
            setTimeout(() => setCopyReceiptLabel('Text Receipt'), 2500);
          } else {
            alert('Failed to copy receipt. Please try again.');
          }
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        alert('Failed to copy receipt. Please try again.');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      customer_id: '',
      vehicles: [{ id: Date.now(), label: '', vehicle_type: 'car', selectedServices: [] }],
      tax_rate: 0,
      tax_enabled: false,
      due_date: '',
      scheduled_time: '09:00',
      location_address: '',
      location_city: '',
      location_state: 'TX',
      location_zip: '',
      notes: ''
    });
    setEditingInvoice(null);
    setShowNewCustomerForm(false);
    setNewCustomerData({
      first_name: '', last_name: '', phone: '', email: '',
      addVehicle: false, vehicle_year: '', vehicle_make: '',
      vehicle_model: '', vehicle_color: '', vehicle_type: 'sedan'
    });
    setCustomerAddresses([]);
    setSelectedAddressId('');
    setNewlyCreatedCustomerId(null);
  };

  const subtotal = calculateSubtotal();
  const taxAmount = subtotal * formData.tax_rate;
  const total = subtotal + taxAmount;

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="invoices-page">
      <div className="page-header">
        <h1 className="page-header__title">Invoices</h1>
        <div className="page-header__actions">
          <button className="btn btn--primary" onClick={() => setShowModal(true)}>
            + Create Invoice
          </button>
        </div>
      </div>

      <div className="invoices-toolbar">
        <div className="filter-tabs">
          {['all', 'draft', 'sent', 'paid', 'overdue'].map((status) => (
            <button
              key={status}
              className={`filter-tab ${filter === status ? 'filter-tab--active' : ''}`}
              onClick={() => setFilter(status)}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {invoices.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state__icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M4 2v20l3-2 3 2 3-2 3 2 3-2 3 2V2l-3 2-3-2-3 2-3-2-3 2-3-2z" />
              <path d="M8 10h8" />
              <path d="M8 14h4" />
            </svg>
          </div>
          <h3 className="empty-state__title">No invoices yet</h3>
          <p>Create your first invoice to get started</p>
          <button className="btn btn--primary mt-md" onClick={() => setShowModal(true)}>
            + Create Invoice
          </button>
        </div>
      ) : (
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Invoice #</th>
                <th>Customer</th>
                <th>Date</th>
                <th>Appointment</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((invoice) => (
                <tr
                  key={invoice.id}
                  className="clickable-row"
                  onClick={() => fetchInvoiceDetail(invoice.id)}
                >
                  <td className="invoice-number">
                    {invoice.invoice_number}
                    {invoice.is_revisit === 1 && (
                      <span title={`Revisit of Invoice #${invoice.revisit_of_invoice_number || ''}`}> 🔁</span>
                    )}
                  </td>
                  <td>{invoice.first_name} {invoice.last_name}</td>
                  <td>{format(new Date(invoice.created_at), 'MMM d, yyyy')}</td>
                  <td>
                    {invoice.appointment_date ? (
                      <>
                        {format(parseISO(invoice.appointment_date), 'MMM d, yyyy')}
                        {invoice.appointment_time && <> at {formatTime12Hour(invoice.appointment_time)}</>}
                      </>
                    ) : invoice.due_date ? (
                      format(parseISO(invoice.due_date), 'MMM d, yyyy')
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className="invoice-amount">${parseFloat(invoice.total).toFixed(2)}</td>
                  <td>
                    <span className={`badge badge--${invoice.status}`}>
                      {invoice.status}
                    </span>
                    {invoice.status === 'paid' && invoice.payment_method && (
                      <span className="payment-method-icon" title={paymentMethods.find(m => m.id === invoice.payment_method)?.name}>
                        {getPaymentMethodDisplay(invoice.payment_method)}
                      </span>
                    )}
                  </td>
                  <td>
                    <div className="invoice-actions" onClick={(e) => e.stopPropagation()}>
                      {invoice.status === 'draft' && (
                        <button
                          className="btn btn--outline btn--small"
                          onClick={() => handleStatusUpdate(invoice.id, 'sent')}
                        >
                          Send
                        </button>
                      )}
                      {invoice.status !== 'paid' && invoice.status !== 'cancelled' && (
                        <button
                          className="btn btn--primary btn--small"
                          onClick={() => handleMarkAsPaidClick(invoice.id)}
                        >
                          Mark Paid
                        </button>
                      )}
                      {invoice.status === 'paid' && (
                        <button
                          className="btn btn--secondary btn--small"
                          onClick={() => handleMarkUnpaid(invoice.id)}
                        >
                          Mark as Unpaid
                        </button>
                      )}
                      <button
                        className="btn btn--outline btn--small"
                        onClick={() => handleCalculateMileage(invoice.id)}
                      >
                        Calculate Mileage
                      </button>
                      {!invoice.is_quote && (
                        <button
                          className="btn btn--outline btn--small"
                          onClick={() => handleOpenRevisit(invoice.id)}
                          title="Couldn't finish the job? Schedule a revisit on a new day"
                        >
                          Revisit
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit Invoice Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => { setShowModal(false); resetForm(); }}
        title={editingInvoice ? "Edit Invoice" : "Create Invoice"}
      >
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Customer *</label>
            {!showNewCustomerForm ? (
              <div className="customer-select-wrapper">
                <CustomerSearchSelect
                  customers={customers}
                  value={formData.customer_id}
                  onChange={(id) => handleCustomerSelect(id)}
                  disabled={!!editingInvoice}
                />
                {!editingInvoice && (
                  <button
                    type="button"
                    className="btn btn--secondary btn--small new-customer-btn"
                    onClick={() => setShowNewCustomerForm(true)}
                  >
                    + New Customer
                  </button>
                )}
              </div>
            ) : (
              <div className="new-customer-form">
                <div className="form-row">
                  <input
                    type="text"
                    className="form-input"
                    placeholder="First Name *"
                    value={newCustomerData.first_name}
                    onChange={(e) => setNewCustomerData({ ...newCustomerData, first_name: e.target.value })}
                  />
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Last Name *"
                    value={newCustomerData.last_name}
                    onChange={(e) => setNewCustomerData({ ...newCustomerData, last_name: e.target.value })}
                  />
                </div>
                <div className="form-row">
                  <input
                    type="tel"
                    className="form-input"
                    placeholder="Phone * (XXX-XXX-XXXX)"
                    value={newCustomerData.phone}
                    onChange={(e) => setNewCustomerData({ ...newCustomerData, phone: formatPhoneNumber(e.target.value) })}
                  />
                  <input
                    type="email"
                    className="form-input"
                    placeholder="Email (optional)"
                    value={newCustomerData.email}
                    onChange={(e) => setNewCustomerData({ ...newCustomerData, email: e.target.value })}
                  />
                </div>

                <div className="add-vehicle-toggle">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={newCustomerData.addVehicle}
                      onChange={(e) => setNewCustomerData({ ...newCustomerData, addVehicle: e.target.checked })}
                    />
                    <span>Add a vehicle for this customer</span>
                  </label>
                </div>

                {newCustomerData.addVehicle && (
                  <div className="vehicle-fields">
                    <div className="form-row form-row--3">
                      <input
                        type="number"
                        className="form-input"
                        placeholder="Year"
                        value={newCustomerData.vehicle_year}
                        onChange={(e) => setNewCustomerData({ ...newCustomerData, vehicle_year: e.target.value })}
                      />
                      <input
                        type="text"
                        className="form-input"
                        placeholder="Make *"
                        value={newCustomerData.vehicle_make}
                        onChange={(e) => setNewCustomerData({ ...newCustomerData, vehicle_make: e.target.value })}
                      />
                      <input
                        type="text"
                        className="form-input"
                        placeholder="Model *"
                        value={newCustomerData.vehicle_model}
                        onChange={(e) => setNewCustomerData({ ...newCustomerData, vehicle_model: e.target.value })}
                      />
                    </div>
                    <div className="form-row">
                      <input
                        type="text"
                        className="form-input"
                        placeholder="Color"
                        value={newCustomerData.vehicle_color}
                        onChange={(e) => setNewCustomerData({ ...newCustomerData, vehicle_color: e.target.value })}
                      />
                      <select
                        className="form-select"
                        value={newCustomerData.vehicle_type}
                        onChange={(e) => setNewCustomerData({ ...newCustomerData, vehicle_type: e.target.value })}
                      >
                        <option value="sedan">Sedan</option>
                        <option value="coupe">Coupe</option>
                        <option value="hatchback">Hatchback</option>
                        <option value="truck">Truck</option>
                        <option value="suv">SUV</option>
                        <option value="van">Van</option>
                        <option value="motorcycle">Motorcycle</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>
                )}

                <div className="new-customer-actions">
                  <button
                    type="button"
                    className="btn btn--secondary btn--small"
                    onClick={() => {
                      setShowNewCustomerForm(false);
                      setNewCustomerData({
                        first_name: '', last_name: '', phone: '', email: '',
                        addVehicle: false, vehicle_year: '', vehicle_make: '',
                        vehicle_model: '', vehicle_color: '', vehicle_type: 'sedan'
                      });
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn--primary btn--small"
                    onClick={handleCreateCustomer}
                  >
                    Add Customer
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Service Date and Time */}
          <div className="form-group">
            <label className="form-label">Appointment Date & Time</label>
            <div className="datetime-inputs">
              <input
                type="date"
                className="form-input"
                value={formData.due_date}
                onInput={(e) => setFormData({ ...formData, due_date: e.target.value })}
                onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                placeholder="Date"
              />
              <input
                type="time"
                className="form-input"
                value={formData.scheduled_time}
                onChange={(e) => setFormData({ ...formData, scheduled_time: e.target.value })}
                placeholder="Time"
                required={!!formData.due_date}
              />
            </div>
            <small className="form-hint">Leave date blank to create invoice without scheduling appointment. Time defaults to 9:00 AM.</small>
          </div>

          {/* Location Fields - only show if date is selected */}
          {formData.due_date && (
            <div className="form-group">
              <label className="form-label">Service Location</label>

              {/* Saved Address Dropdown - only show if customer has saved addresses */}
              {customerAddresses.length > 0 && (
                <div style={{ marginBottom: '0.5rem' }}>
                  <select
                    className="form-select"
                    value={selectedAddressId}
                    onChange={(e) => handleAddressSelect(e.target.value)}
                  >
                    {customerAddresses.map((addr) => (
                      <option key={addr.id} value={addr.id}>
                        {addr.name} - {addr.address}{addr.city && `, ${addr.city}`}
                      </option>
                    ))}
                    <option value="custom">+ Enter a different address</option>
                  </select>
                </div>
              )}

              <input
                type="text"
                className="form-input"
                placeholder="Street Address"
                value={formData.location_address}
                onChange={(e) => {
                  setFormData({ ...formData, location_address: e.target.value });
                  if (customerAddresses.length > 0) setSelectedAddressId('custom');
                }}
              />
              <div className="form-row" style={{ marginTop: '0.5rem' }}>
                <input
                  type="text"
                  className="form-input"
                  placeholder="City"
                  value={formData.location_city}
                  onChange={(e) => {
                    setFormData({ ...formData, location_city: e.target.value });
                    if (customerAddresses.length > 0) setSelectedAddressId('custom');
                  }}
                />
                <input
                  type="text"
                  className="form-input"
                  placeholder="State"
                  value={formData.location_state}
                  onChange={(e) => {
                    setFormData({ ...formData, location_state: e.target.value });
                    if (customerAddresses.length > 0) setSelectedAddressId('custom');
                  }}
                  maxLength="2"
                  style={{ width: '80px' }}
                />
                <input
                  type="text"
                  className="form-input"
                  placeholder="ZIP"
                  value={formData.location_zip}
                  onChange={(e) => {
                    setFormData({ ...formData, location_zip: e.target.value });
                    if (customerAddresses.length > 0) setSelectedAddressId('custom');
                  }}
                  maxLength="5"
                  style={{ width: '100px' }}
                />
              </div>
              <small className="form-hint">
                {customerAddresses.length > 0
                  ? 'Select a saved address or enter a custom one'
                  : 'Location is used for weather forecast display on the calendar'}
              </small>
            </div>
          )}

          {/* Vehicle Sections */}
          {formData.vehicles.map((vehicle, vIndex) => (
            <div key={vehicle.id} className={`vehicle-section${formData.vehicles.length > 1 ? ' vehicle-section--bordered' : ''}`}>
              {formData.vehicles.length > 1 && (
                <div className="vehicle-section__header">
                  <span className="vehicle-section__title">Vehicle {vIndex + 1}</span>
                  <button
                    type="button"
                    className="btn btn--danger btn--small"
                    onClick={() => removeVehicle(vIndex)}
                  >
                    Remove
                  </button>
                </div>
              )}

              {/* Vehicle Label */}
              <div className="form-group">
                <input
                  type="text"
                  className="form-input"
                  placeholder='Vehicle label (e.g. Tesla, Wife&apos;s Porsche...)'
                  value={vehicle.label}
                  onChange={(e) => updateVehicleLabel(vIndex, e.target.value)}
                />
                <small className="form-hint">Optional — appears on the invoice before each service (e.g. "Tesla — Full Detail")</small>
              </div>

              {/* Vehicle Type */}
              <div className="form-group">
                <label className="form-label">Vehicle Type</label>
                <div className="vehicle-type-selector">
                  <button
                    type="button"
                    className={`vehicle-type-btn ${vehicle.vehicle_type === 'car' ? 'vehicle-type-btn--active' : ''}`}
                    onClick={() => handleVehicleTypeChange(vIndex, 'car')}
                  >
                    Car
                  </button>
                  <button
                    type="button"
                    className={`vehicle-type-btn ${vehicle.vehicle_type === 'truck' ? 'vehicle-type-btn--active' : ''}`}
                    onClick={() => handleVehicleTypeChange(vIndex, 'truck')}
                  >
                    SUV / Truck
                  </button>
                  <button
                    type="button"
                    className={`vehicle-type-btn ${vehicle.vehicle_type !== 'car' && vehicle.vehicle_type !== 'truck' ? 'vehicle-type-btn--active' : ''}`}
                    onClick={() => handleVehicleTypeChange(vIndex, 'other')}
                  >
                    Other
                  </button>
                </div>
                {vehicle.vehicle_type !== 'car' && vehicle.vehicle_type !== 'truck' && (
                  <input
                    type="text"
                    className="form-input vehicle-type-input"
                    placeholder="Enter vehicle type (e.g., Motorcycle, RV, Boat)"
                    value={vehicle.vehicle_type === 'other' ? '' : vehicle.vehicle_type}
                    onChange={(e) => {
                      const updatedVehicles = formData.vehicles.map((v, i) =>
                        i === vIndex ? { ...v, vehicle_type: e.target.value || 'other' } : v
                      );
                      setFormData({ ...formData, vehicles: updatedVehicles });
                    }}
                  />
                )}
              </div>

              {/* Packages */}
              <div className="form-group">
                <label className="form-label">Packages</label>
                <div className="service-selector">
                  {services.packages.map((service) => {
                    const currentPrice = vehicle.vehicle_type === 'truck'
                      ? (service.base_price_truck || service.base_price_car)
                      : service.base_price_car;

                    return (
                      <div
                        key={service.id}
                        className={`service-card ${isServiceSelected(vIndex, service.id) ? 'service-card--selected' : ''}`}
                        onClick={() => toggleService(vIndex, service)}
                      >
                        <div className="service-card__header">
                          <span className="service-card__name">{service.name}</span>
                          {service.base_price_truck && service.base_price_truck !== service.base_price_car ? (
                            <div className="service-card__pricing-simple">
                              <span className={`service-card__price ${vehicle.vehicle_type === 'car' ? 'service-card__price--active' : ''}`}>
                                ${service.base_price_car}
                              </span>
                              <span className="service-card__price-separator">/</span>
                              <span className={`service-card__price ${vehicle.vehicle_type === 'truck' ? 'service-card__price--active' : ''}`}>
                                ${service.base_price_truck}
                              </span>
                            </div>
                          ) : (
                            <span className="service-card__price">${service.base_price_car}</span>
                          )}
                        </div>
                        {service.description && (
                          <p className="service-card__desc">{service.description}</p>
                        )}
                        {isServiceSelected(vIndex, service.id) && (
                          <div className="service-card__check">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/>
                            </svg>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Add-ons */}
              <div className="form-group">
                <label className="form-label">Add-ons</label>
                <div className="service-selector service-selector--addons">
                  {services.addons.map((service) => {
                    const currentPrice = vehicle.vehicle_type === 'truck'
                      ? (service.base_price_truck || service.base_price_car)
                      : service.base_price_car;

                    return (
                      <div
                        key={service.id}
                        className={`addon-chip ${isServiceSelected(vIndex, service.id) ? 'addon-chip--selected' : ''}`}
                        onClick={() => toggleService(vIndex, service)}
                      >
                        <span className="addon-chip__name">{service.name}</span>
                        <span className="addon-chip__price">+${currentPrice}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Add Tip */}
              {!isServiceSelected(vIndex, 'tip') && (
                <div className="form-group">
                  <button
                    type="button"
                    className="btn btn--outline btn--small"
                    onClick={() => toggleService(vIndex, {
                      id: 'tip',
                      name: 'Added Tip',
                      category: 'tip',
                      base_price_car: 0,
                      base_price_truck: 0
                    })}
                  >
                    + Add Tip
                  </button>
                </div>
              )}

              {/* Comp / Giveaway - zeroes out this vehicle's total while keeping line items visible */}
              {!isServiceSelected(vIndex, 'comp_discount') && (
                <div className="form-group">
                  <button
                    type="button"
                    className="btn btn--outline btn--small"
                    onClick={() => {
                      const vehicleSubtotal = vehicle.selectedServices.reduce((sum, s) => sum + (s.price || 0), 0);
                      toggleService(vIndex, {
                        id: 'comp_discount',
                        name: 'Comped — Raffle/Giveaway Prize (No Charge)',
                        category: 'discount',
                        base_price_car: -vehicleSubtotal,
                        base_price_truck: -vehicleSubtotal
                      });
                    }}
                  >
                    🎁 Comp / Giveaway
                  </button>
                </div>
              )}

              {/* Selected Items for this vehicle */}
              {vehicle.selectedServices.length > 0 && (
                <div className="selected-services">
                  <label className="form-label">Selected Items</label>
                  {vehicle.selectedServices.map((service, sIndex) => (
                    <div key={service.id ?? sIndex} className="selected-service-item">
                      <div className="selected-service-row">
                        <span className="selected-service-name">
                          {service.category === 'addon' && '+ '}
                          {service.name}
                        </span>
                        <div className="selected-service-price">
                          <span>$</span>
                          <input
                            type="number"
                            step="0.01"
                            value={service.price}
                            onChange={(e) => updateServicePrice(vIndex, service.id, e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            className="price-input"
                          />
                        </div>
                        <button
                          type="button"
                          className="btn btn--danger btn--icon"
                          onClick={() => toggleService(vIndex, service)}
                        >
                          &times;
                        </button>
                      </div>
                      {service.name === 'Custom Inquiry' && (
                        <input
                          type="text"
                          className="form-input custom-inquiry-note"
                          placeholder="Briefly describe the job (shows on invoice)"
                          value={service.customNote || ''}
                          onChange={(e) => updateServiceNote(vIndex, service.id, e.target.value)}
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}

          {/* Add Another Vehicle */}
          <div className="form-group">
            <button
              type="button"
              className="btn btn--secondary"
              onClick={addVehicle}
            >
              + Add Another Vehicle
            </button>
          </div>

          <div className="invoice-summary">
            <div className="invoice-summary__row">
              <span>Subtotal</span>
              <span>${subtotal.toFixed(2)}</span>
            </div>
            <div className="invoice-summary__row invoice-summary__row--total">
              <span>Total</span>
              <span>${total.toFixed(2)}</span>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Notes</label>
            <textarea
              className="form-textarea"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows="2"
            />
          </div>

          <div className="modal__footer">
            <button type="button" className="btn btn--secondary" onClick={() => { setShowModal(false); resetForm(); }}>
              Cancel
            </button>
            <button type="submit" className="btn btn--primary" disabled={submitting}>
              {submitting ? 'Saving...' : (editingInvoice ? 'Update Invoice' : 'Create Invoice')}
            </button>
          </div>
        </form>
      </Modal>

      {/* Invoice Detail Modal */}
      <Modal
        isOpen={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        title={`Invoice ${selectedInvoice?.invoice_number || ''}`}
      >
        {selectedInvoice && (
          <div className="invoice-detail">
            <div className="invoice-detail__header">
              <div>
                <h3 className="invoice-detail__company">POLARIS</h3>
                <p className="invoice-detail__tagline">Professional Detailing</p>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                {selectedInvoice.is_revisit === 1 && (
                  <span
                    className="badge"
                    style={{ backgroundColor: 'rgba(201, 162, 39, 0.2)', color: 'var(--color-gold)' }}
                    title={`Revisit of Invoice #${selectedInvoice.revisit_of_invoice_number || ''}`}
                  >
                    🔁 Revisit
                  </span>
                )}
                <span className={`badge badge--${selectedInvoice.status}`}>
                  {selectedInvoice.status}
                </span>
                {selectedInvoice.status === 'paid' && selectedInvoice.payment_method && (
                  <span className="payment-method-icon payment-method-icon--detail" title={paymentMethods.find(m => m.id === selectedInvoice.payment_method)?.name}>
                    {getPaymentMethodDisplay(selectedInvoice.payment_method)}
                  </span>
                )}
              </div>
            </div>

            <div className="invoice-detail__info">
              <div>
                <p className="invoice-detail__label">Bill To</p>
                <p className="invoice-detail__value">
                  {selectedInvoice.first_name} {selectedInvoice.last_name}
                </p>
                {selectedInvoice.address && (
                  <p className="invoice-detail__value">
                    {selectedInvoice.address}<br />
                    {selectedInvoice.city}, {selectedInvoice.state} {selectedInvoice.zip}
                  </p>
                )}
              </div>
              <div>
                <p className="invoice-detail__label">Invoice Date</p>
                <p className="invoice-detail__value">
                  {format(new Date(selectedInvoice.created_at), 'MMMM d, yyyy')}
                </p>
                {selectedInvoice.appointment && (
                  <>
                    <p className="invoice-detail__label">Appointment Date & Time</p>
                    <p className="invoice-detail__value">
                      {format(parseISO(selectedInvoice.appointment.scheduled_date), 'MMMM d, yyyy')} at {formatTime12Hour(selectedInvoice.appointment.scheduled_time)}
                    </p>
                    <p className="invoice-detail__value">
                      <span className={`badge badge--${selectedInvoice.appointment.status}`}>
                        {selectedInvoice.appointment.status}
                      </span>
                    </p>
                  </>
                )}
                {selectedInvoice.due_date && !selectedInvoice.appointment && (
                  <>
                    <p className="invoice-detail__label">Service Date</p>
                    <p className="invoice-detail__value">
                      {format(parseISO(selectedInvoice.due_date), 'MMMM d, yyyy')}
                    </p>
                  </>
                )}
                {selectedInvoice.emailed_at && (
                  <>
                    <p className="invoice-detail__label">Last Emailed</p>
                    <p className="invoice-detail__value">
                      {format(new Date(selectedInvoice.emailed_at), 'MMM d, yyyy h:mm a')}
                    </p>
                  </>
                )}
              </div>
            </div>

            <table className="table invoice-detail__table">
              <thead>
                <tr>
                  <th>Description</th>
                  <th>Qty</th>
                  <th>Price</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {selectedInvoice.items?.map((item, index) => (
                  <tr key={index}>
                    <td>{item.description}</td>
                    <td>{item.quantity}</td>
                    <td>${parseFloat(item.unit_price).toFixed(2)}</td>
                    <td>${parseFloat(item.total).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="invoice-summary">
              <div className="invoice-summary__row">
                <span>Subtotal</span>
                <span>${parseFloat(selectedInvoice.subtotal).toFixed(2)}</span>
              </div>
              <div className="invoice-summary__row">
                <span>Tax ({(selectedInvoice.tax_rate * 100).toFixed(2)}%)</span>
                <span>${parseFloat(selectedInvoice.tax_amount).toFixed(2)}</span>
              </div>
              <div className="invoice-summary__row invoice-summary__row--total">
                <span>Total</span>
                <span>${parseFloat(selectedInvoice.total).toFixed(2)}</span>
              </div>
              {selectedInvoice.tip_amount > 0 && (
                <>
                  <div className="invoice-summary__row">
                    <span>Tip</span>
                    <span>${parseFloat(selectedInvoice.tip_amount).toFixed(2)}</span>
                  </div>
                  <div className="invoice-summary__row invoice-summary__row--total">
                    <span>Total Collected</span>
                    <span>${(parseFloat(selectedInvoice.total) + parseFloat(selectedInvoice.tip_amount)).toFixed(2)}</span>
                  </div>
                </>
              )}
            </div>

            {selectedInvoice.notes && (
              <div className="invoice-detail__notes">
                <p className="invoice-detail__label">Notes</p>
                <p>{selectedInvoice.notes}</p>
              </div>
            )}

            <div className="modal__footer">
              <button
                className="btn btn--danger"
                onClick={() => handleDeleteInvoice(selectedInvoice.id)}
              >
                Delete
              </button>
              <button
                className="btn btn--outline"
                onClick={() => handleEditInvoice(selectedInvoice)}
              >
                Edit Invoice
              </button>
              {!selectedInvoice.is_quote && (
                <button
                  className="btn btn--outline"
                  onClick={() => handleOpenRevisit(selectedInvoice.id)}
                  title="Couldn't finish the job? Schedule a revisit on a new day"
                >
                  Revisit
                </button>
              )}
              {selectedInvoice.email && (
                <button
                  className="btn btn--primary"
                  onClick={() => handleSendEmail(selectedInvoice.id, false)}
                  disabled={sendingEmail || !gmailConnected}
                  title={!gmailConnected ? 'Connect Gmail in Settings first' : `Send to ${selectedInvoice.email}`}
                >
                  {sendingEmail ? 'Sending...' : (selectedInvoice.emailed_at ? 'Resend Invoice' : 'Email Invoice')}
                </button>
              )}
              {selectedInvoice.email && (
                <button
                  className="btn btn--outline"
                  onClick={() => handleSendEmail(selectedInvoice.id, true)}
                  disabled={sendingEmail || !gmailConnected}
                  title={!gmailConnected ? 'Connect Gmail in Settings first' : `Send receipt to ${selectedInvoice.email}`}
                >
                  {sendingEmail ? 'Sending...' : 'Email Receipt'}
                </button>
              )}
              <button
                className="btn btn--outline"
                onClick={() => handleCopyPaymentLink(selectedInvoice.id)}
              >
                {copyLinkLabel}
              </button>
              {selectedInvoice.status === 'paid' && (
                <button
                  className="btn btn--outline"
                  onClick={() => handleCopyReceipt(selectedInvoice.id)}
                >
                  {copyReceiptLabel}
                </button>
              )}
              {selectedInvoice.status !== 'paid' && selectedInvoice.status !== 'cancelled' && (
                <button
                  className="btn btn--primary"
                  onClick={() => handleMarkAsPaidClick(selectedInvoice.id)}
                >
                  Mark as Paid
                </button>
              )}
              {selectedInvoice.status === 'paid' && (
                <button
                  className="btn btn--secondary"
                  onClick={() => handleMarkUnpaid(selectedInvoice.id)}
                >
                  Mark as Unpaid
                </button>
              )}
              {selectedInvoice.status === 'sent' && (
                <button
                  className="btn btn--secondary"
                  onClick={() => handleStatusUpdate(selectedInvoice.id, 'draft')}
                  title="Reverts to Draft — use this if the email never actually reached the customer"
                >
                  Unmark as Sent
                </button>
              )}
              <button
                className="btn btn--secondary"
                onClick={() => setShowDetailModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Revisit Modal — pick a new day to finish the job */}
      <Modal
        isOpen={showRevisitModal}
        onClose={() => setShowRevisitModal(false)}
        title="Schedule a Revisit"
      >
        <form onSubmit={handleSubmitRevisit}>
          <p className="revisit-hint">
            This copies the invoice and its appointment to a new day, with a note that it's a revisit to finish the job.
          </p>
          <div className="form-group">
            <label className="form-label">New Date *</label>
            <input
              type="date"
              className="form-input"
              value={revisitDate}
              onChange={(e) => setRevisitDate(e.target.value)}
              required
            />
          </div>
          <div className="modal__footer">
            <button type="button" className="btn btn--secondary" onClick={() => setShowRevisitModal(false)}>
              Cancel
            </button>
            <button type="submit" className="btn btn--primary" disabled={submittingRevisit}>
              {submittingRevisit ? 'Scheduling...' : 'Schedule Revisit'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Conflict Confirmation Modal */}
      <Modal
        isOpen={showConflictModal}
        onClose={handleCancelConflict}
        title="Schedule Conflict"
      >
        <div className="conflict-warning">
          <p className="conflict-warning__message">
            This appointment may conflict with existing appointments:
          </p>
          <ul className="conflict-warning__list">
            {conflictingAppointments.map((apt) => (
              <li key={apt.id} className="conflict-warning__item">
                <strong>{apt.first_name} {apt.last_name}</strong>
                {apt.location_city && ` - ${apt.location_city}`}
              </li>
            ))}
          </ul>
          <p className="conflict-warning__question">
            Are you sure you want to schedule it?
          </p>
          <div className="modal__footer">
            <button
              type="button"
              className="btn btn--secondary"
              onClick={handleCancelConflict}
            >
              No
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={handleConfirmConflict}
            >
              Yes
            </button>
          </div>
        </div>
      </Modal>

      {/* Payment Method Modal */}
      <Modal
        isOpen={showPaymentModal}
        onClose={() => { setShowPaymentModal(false); setPayingInvoiceId(null); }}
        title="Select Payment Method"
      >
        <div className="payment-methods">
          <p className="payment-methods__subtitle">How did the customer pay?</p>
          <div className="payment-methods__grid">
            {paymentMethods.map((method) => (
              <button
                key={method.id}
                className="payment-method-btn"
                onClick={() => handlePaymentMethodSelect(method.id)}
                style={{ '--method-color': method.color }}
              >
                <span className="payment-method-btn__icon">
                  {method.icon ? (
                    <span style={{ fontSize: '1.5rem' }}>{method.icon}</span>
                  ) : method.logo === 'zelle' ? (
                    <svg viewBox="0 0 24 24" width="28" height="28" fill={method.color}>
                      <path d="M13.559 24h-2.841a.483.483 0 0 1-.483-.483v-6.628H5.685a.69.69 0 0 1-.493-1.173l9.532-9.716a.483.483 0 0 1 .827.342v6.379h4.765a.69.69 0 0 1 .492 1.173l-6.766 6.902v2.721a.483.483 0 0 1-.483.483zM7.616 15.345h3.86c.267 0 .483.217.483.484v4.972l4.834-4.934h-4.075a.483.483 0 0 1-.483-.483V9.799l-4.619 5.546z"/>
                    </svg>
                  ) : method.logo === 'cashapp' ? (
                    <svg viewBox="0 0 24 24" width="28" height="28" fill={method.color}>
                      <path d="M23.59 3.47A5.1 5.1 0 0 0 20.53.41C19.63.14 18.46 0 17 0H7C5.54 0 4.37.14 3.47.41A5.1 5.1 0 0 0 .41 3.47C.14 4.37 0 5.54 0 7v10c0 1.46.14 2.63.41 3.53a5.1 5.1 0 0 0 3.06 3.06c.9.27 2.07.41 3.53.41h10c1.46 0 2.63-.14 3.53-.41a5.1 5.1 0 0 0 3.06-3.06c.27-.9.41-2.07.41-3.53V7c0-1.46-.14-2.63-.41-3.53zm-6.17 6.72l-.6.6a.47.47 0 0 1-.6.06 2.8 2.8 0 0 0-1.47-.42c-.55 0-1.04.15-1.32.45-.28.28-.32.56-.32.73 0 .21.04.39.2.55.17.18.52.36 1.04.54l.9.3c.74.24 1.38.54 1.87 1 .5.48.79 1.13.79 1.96 0 .9-.27 1.68-.98 2.29-.7.6-1.6.92-2.72.98v1.06a.47.47 0 0 1-.47.47h-.9a.47.47 0 0 1-.48-.47v-1.1a5.04 5.04 0 0 1-2.4-.95.47.47 0 0 1-.05-.67l.6-.6a.47.47 0 0 1 .61-.05 3.42 3.42 0 0 0 1.81.54c.62 0 1.1-.14 1.4-.4.28-.26.42-.58.42-.97 0-.27-.08-.48-.25-.67-.2-.2-.56-.4-1.12-.58l-.87-.29c-.7-.23-1.3-.55-1.77-1a2.4 2.4 0 0 1-.72-1.84c0-.86.3-1.57.92-2.14.61-.57 1.45-.88 2.5-.93V6.19c0-.26.21-.47.47-.47h.9c.27 0 .48.21.48.47v1.03a4.63 4.63 0 0 1 2.07.73c.2.13.24.4.1.6z"/>
                    </svg>
                  ) : method.logo === 'venmo' ? (
                    <svg viewBox="0 0 24 24" width="28" height="28" fill={method.color}>
                      <path d="M19.616 2.252c.756 1.26 1.092 2.553 1.092 4.185 0 5.22-4.431 11.982-8.04 16.729H5.25L2.291 3.844l6.156-.605 1.671 13.564c1.545-2.533 3.457-6.493 3.457-9.2 0-1.564-.267-2.635-.629-3.513l6.67-1.838z"/>
                    </svg>
                  ) : method.logo === 'paypal' ? (
                    <svg viewBox="0 0 24 24" width="28" height="28" fill={method.color}>
                      <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 0 0-.607-.541c-.013.076-.026.175-.041.254-.93 4.778-4.005 7.201-9.138 7.201h-2.19a.563.563 0 0 0-.556.479l-1.187 7.527h-.506l-.24 1.516a.56.56 0 0 0 .554.647h3.882c.46 0 .85-.334.922-.788.06-.26.76-4.852.816-5.09a.932.932 0 0 1 .923-.788h.58c3.76 0 6.705-1.528 7.565-5.946.36-1.847.174-3.388-.777-4.471z"/>
                    </svg>
                  ) : method.logo === 'apple' ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: '#000', borderRadius: '6px', padding: '2px' }}>
                      <svg viewBox="0 0 24 24" width="24" height="24" fill="white">
                        <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701"/>
                      </svg>
                    </span>
                  ) : null}
                </span>
                <span className="payment-method-btn__name">{method.name}</span>
              </button>
            ))}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Invoices;
